<template>
    <div class="fixed inset-0 z-[70]" aria-labelledby="modal-title" role="dialog" aria-modal="true">
        <div class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"></div>
        <div class="fixed inset-0 overflow-hidden">
            <div class="absolute inset-0">
                <div class="h-full w-full bg-white">
                    <!-- 헤더 -->
                    <div class="fixed top-0 left-0 right-0 z-10 bg-white border-b border-gray-200">
                        <div class="max-w-5xl mx-auto">
                            <div class="flex items-center justify-between px-4 py-2 sm:py-3">
                                <div class="flex items-center gap-2 sm:gap-3 min-w-0">
                                    <button @click="goBack"
                                        class="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100">
                                        <ArrowLeftIcon class="w-4 h-4 sm:w-5 sm:h-5" />
                                    </button>
                                    <h2 class="text-sm sm:text-lg font-semibold text-gray-900">새 이력서 작성</h2>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 메인 컨텐츠 -->
                    <div class="pt-[60px] h-screen overflow-y-auto">
                        <div class="max-w-4xl mx-auto px-3 py-4 sm:px-6 sm:py-8">
                            <!-- 기본 정보 -->
                            <div class="mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-gray-200">
                                <div class="flex flex-col sm:flex-row gap-4 sm:gap-8">
                                    <div
                                        class="w-28 h-36 sm:w-40 sm:h-48 mx-auto sm:mx-0 flex-shrink-0 border border-gray-200 rounded-lg overflow-hidden">
                                        <div class="w-full h-full flex items-center justify-center bg-gray-50 cursor-pointer"
                                            @click="$refs.profileInput.click()">
                                            <UserIcon v-if="!previewImage"
                                                class="w-7 h-7 sm:w-12 sm:h-12 text-gray-300" />
                                            <img v-else :src="previewImage" alt="프로필 미리보기"
                                                class="w-full h-full object-cover" />
                                        </div>
                                        <input type="file" ref="profileInput" class="hidden" accept="image/*"
                                            @change="handleProfileImageChange" />
                                    </div>
                                    <div class="flex-1">
                                        <div class="grid grid-cols-2 gap-2 sm:gap-x-8 sm:gap-y-4">
                                            <div class="w-full">
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">이름</div>
                                                <input v-model="formData.name" type="text"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-xs sm:text-sm"
                                                    placeholder="이름을 입력하세요" />
                                            </div>
                                            <div class="w-full">
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">성별</div>
                                                <select v-model="formData.sex"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-xs sm:text-sm">
                                                    <option value="0">남성</option>
                                                    <option value="1">여성</option>
                                                </select>
                                            </div>
                                            <div class="w-full">
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">생년월일</div>
                                                <div class="relative">
                                                    <input v-model="formData.dob" type="text" maxlength="10"
                                                        class="w-full p-1.5 sm:p-2 pl-8 sm:pl-10 border rounded-lg text-xs sm:text-sm transition-all duration-200"
                                                        :class="{ '!border-red-500': !validateDate(formData.dob) && formData.dob }"
                                                        placeholder="YYYY.MM.DD" @input="validateBirthDate" />
                                                    <CalendarIcon
                                                        class="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-2 sm:left-2.5 top-1/2 transform -translate-y-1/2" />
                                                </div>
                                                <div v-if="!validateDate(formData.dob) && formData.dob"
                                                    class="text-xs text-red-500 mt-1 block">올바른 생년월일 형식이 아닙니다
                                                    (YYYY.MM.DD)
                                                </div>
                                            </div>
                                            <div class="w-full">
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">연락처</div>
                                                <input v-model="formData.hp" type="tel"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-xs sm:text-sm"
                                                    placeholder="010-0000-0000" @input="validatePhoneNumber" />
                                            </div>
                                            <div class="w-full col-span-2">
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">주소</div>
                                                <input v-model="formData.address" type="text"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-xs sm:text-sm"
                                                    placeholder="주소를 입력하세요" />
                                            </div>
                                            <div class="w-full col-span-2">
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">이메일</div>
                                                <input v-model="formData.email" type="email"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-xs sm:text-sm"
                                                    placeholder="example@email.com" />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- 학력 사항 -->
                            <div class="mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-gray-200">
                                <div class="flex items-center justify-between mb-2 sm:mb-3">
                                    <div class="flex items-center">
                                        <h3 class="text-sm sm:text-base font-semibold text-gray-900">학력 사항</h3>
                                        <span class="ml-1 sm:ml-2 text-xs text-gray-500">({{ formData.education.length
                                            }})</span>
                                    </div>
                                    <button @click="addEducation"
                                        class="text-xs sm:text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center">
                                        <PlusIcon class="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-0.5 sm:mr-1" />
                                        추가
                                    </button>
                                </div>
                                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    <div v-for="(edu, index) in formData.education" :key="index"
                                        class="border border-gray-200 rounded-lg p-2 sm:p-4 max-w-[320px] w-full">
                                        <div class="grid grid-cols-1 gap-2">
                                            <div>
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">학교명</div>
                                                <input v-model="edu.school" type="text"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-xs sm:text-sm"
                                                    placeholder="학교명을 입력하세요" />
                                            </div>
                                            <div>
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">전공</div>
                                                <input v-model="edu.major" type="text"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-xs sm:text-sm"
                                                    placeholder="전공을 입력하세요" />
                                            </div>
                                            <div>
                                                <div class="flex items-center justify-between mb-1">
                                                    <div class="text-xs sm:text-sm text-gray-600">재학기간</div>
                                                    <label
                                                        class="flex items-center gap-1 text-xs sm:text-sm text-gray-600 cursor-pointer">
                                                        <input type="checkbox" v-model="edu.isStudying"
                                                            @change="handleStudyingChange(edu)"
                                                            class="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                                                        재학중
                                                    </label>
                                                </div>
                                                <div class="flex items-center gap-1">
                                                    <div class="relative flex-1">
                                                        <input v-model="edu.startDate" type="text"
                                                            @input="validateEduStartDate($event, edu)"
                                                            class="w-full p-1.5 sm:p-2 pl-8 sm:pl-10 border rounded-lg text-xs sm:text-sm transition-all duration-200"
                                                            :class="{ '!border-red-500': !validateYearMonth(edu.startDate).isValid && edu.startDate }"
                                                            placeholder="YYYY.MM" maxlength="7" />
                                                        <CalendarIcon
                                                            class="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-2 sm:left-2.5 top-1/2 transform -translate-y-1/2" />
                                                    </div>
                                                    <span class="text-gray-400">~</span>
                                                    <div class="relative flex-1">
                                                        <input v-model="edu.endDate" type="text"
                                                            @input="validateEduEndDate($event, edu)"
                                                            class="w-full p-1.5 sm:p-2 pl-8 sm:pl-10 border rounded-lg text-xs sm:text-sm transition-all duration-200"
                                                            :class="{ '!border-red-500': !validateYearMonth(edu.endDate).isValid && edu.endDate && edu.endDate !== '재학중' }"
                                                            placeholder="YYYY.MM" maxlength="7"
                                                            :disabled="edu.isStudying" />
                                                        <CalendarIcon
                                                            class="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-2 sm:left-2.5 top-1/2 transform -translate-y-1/2" />
                                                    </div>
                                                </div>
                                                <div v-if="!validateYearMonth(edu.startDate).isValid && edu.startDate"
                                                    class="text-xs text-red-500 mt-1 block">올바른 입학일 형식이 아닙니다 (YYYY.MM)
                                                </div>
                                                <div v-if="!validateYearMonth(edu.endDate).isValid && edu.endDate && edu.endDate !== '재학중'"
                                                    class="text-xs text-red-500 mt-1 block">올바른 졸업일 형식이 아닙니다 (YYYY.MM)
                                                </div>
                                            </div>
                                        </div>
                                        <button @click="removeEducation(index)"
                                            class="mt-2 sm:mt-4 text-xs sm:text-sm text-red-600 hover:text-red-700 flex items-center">
                                            <TrashIcon class="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" />
                                            삭제
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- 경력 사항 -->
                            <div class="mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-gray-200">
                                <div class="flex items-center justify-between mb-2 sm:mb-3">
                                    <div class="flex items-center">
                                        <h3 class="text-sm sm:text-base font-semibold text-gray-900">경력 사항</h3>
                                        <span class="ml-1 sm:ml-2 text-xs text-gray-500">({{ formData.careers.length
                                            }})</span>
                                    </div>
                                    <button @click="addCareer"
                                        class="text-xs sm:text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center">
                                        <PlusIcon class="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-0.5 sm:mr-1" />
                                        추가
                                    </button>
                                </div>
                                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    <div v-for="(career, index) in formData.careers" :key="index"
                                        class="border border-gray-200 rounded-lg p-2 sm:p-4 max-w-[320px] w-full">
                                        <div class="grid grid-cols-1 gap-2">
                                            <div>
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">회사명</div>
                                                <input v-model="career.company" type="text"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-xs sm:text-sm"
                                                    placeholder="회사명을 입력하세요" />
                                            </div>
                                            <div>
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">직위</div>
                                                <input v-model="career.position" type="text"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-xs sm:text-sm"
                                                    placeholder="직위를 입력하세요" />
                                            </div>
                                            <div>
                                                <div class="flex items-center justify-between mb-1">
                                                    <div class="text-xs sm:text-sm text-gray-600">재직기간</div>
                                                    <label
                                                        class="flex items-center gap-1 text-xs sm:text-sm text-gray-600 cursor-pointer">
                                                        <input type="checkbox" v-model="career.isWorking"
                                                            @change="handleWorkingChange(career)"
                                                            class="w-3 h-3 sm:w-4 sm:h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500" />
                                                        재직중
                                                    </label>
                                                </div>
                                                <div class="flex items-center gap-1">
                                                    <div class="relative flex-1">
                                                        <input v-model="career.startDate" type="text"
                                                            @input="validateCareerStartDate($event, career)"
                                                            class="w-full p-1.5 sm:p-2 pl-8 sm:pl-10 border rounded-lg text-xs sm:text-sm transition-all duration-200"
                                                            :class="{ '!border-red-500': !validateYearMonth(career.startDate).isValid && career.startDate }"
                                                            placeholder="YYYY.MM" maxlength="7" />
                                                        <CalendarIcon
                                                            class="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-2 sm:left-2.5 top-1/2 transform -translate-y-1/2" />
                                                    </div>
                                                    <span class="text-gray-400">~</span>
                                                    <div class="relative flex-1">
                                                        <input v-model="career.endDate" type="text"
                                                            @input="validateCareerEndDate($event, career)"
                                                            class="w-full p-1.5 sm:p-2 pl-8 sm:pl-10 border rounded-lg text-xs sm:text-sm transition-all duration-200"
                                                            :class="{ '!border-red-500': !validateYearMonth(career.endDate).isValid && career.endDate && career.endDate !== '재직중' }"
                                                            placeholder="YYYY.MM" maxlength="7"
                                                            :disabled="career.isWorking" />
                                                        <CalendarIcon
                                                            class="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-2 sm:left-2.5 top-1/2 transform -translate-y-1/2" />
                                                    </div>
                                                </div>
                                                <div v-if="!validateYearMonth(career.startDate).isValid && career.startDate"
                                                    class="text-xs text-red-500 mt-1 block">올바른 입사일 형식이 아닙니다 (YYYY.MM)
                                                </div>
                                                <div v-if="!validateYearMonth(career.endDate).isValid && career.endDate && career.endDate !== '재직중'"
                                                    class="text-xs text-red-500 mt-1 block">올바른 퇴사일 형식이 아닙니다 (YYYY.MM)
                                                </div>
                                            </div>
                                        </div>
                                        <button @click="removeCareer(index)"
                                            class="mt-2 sm:mt-4 text-xs sm:text-sm text-red-600 hover:text-red-700 flex items-center">
                                            <TrashIcon class="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" />
                                            삭제
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- 자격 사항 -->
                            <div class="mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-gray-200">
                                <div class="flex items-center justify-between mb-2 sm:mb-3">
                                    <div class="flex items-center">
                                        <h3 class="text-sm sm:text-base font-semibold text-gray-900">자격 사항</h3>
                                        <span class="ml-1 sm:ml-2 text-xs text-gray-500">({{
                                            formData.certifications.length
                                            }})</span>
                                    </div>
                                    <button @click="addCertification"
                                        class="text-xs sm:text-sm font-medium text-blue-600 hover:text-blue-700 flex items-center">
                                        <PlusIcon class="w-3 h-3 sm:w-3.5 sm:h-3.5 mr-0.5 sm:mr-1" />
                                        추가
                                    </button>
                                </div>
                                <div class="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                                    <div v-for="(cert, index) in formData.certifications" :key="index"
                                        class="border border-gray-200 rounded-lg p-2 sm:p-4 max-w-[320px] w-full">
                                        <div class="grid grid-cols-1 gap-2">
                                            <div>
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">자격증명</div>
                                                <input v-model="cert.name" type="text"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-xs sm:text-sm"
                                                    placeholder="자격증명을 입력하세요" />
                                            </div>
                                            <div>
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">발급기관</div>
                                                <input v-model="cert.organization" type="text"
                                                    class="w-full p-1.5 sm:p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 text-xs sm:text-sm"
                                                    placeholder="발급기관을 입력하세요" />
                                            </div>
                                            <div>
                                                <div class="text-xs sm:text-sm text-gray-600 mb-1">취득일</div>
                                                <div class="relative">
                                                    <input v-model="cert.date" type="text"
                                                        @input="validateCertDate($event, cert)"
                                                        class="w-full p-1.5 sm:p-2 pl-8 sm:pl-10 border rounded-lg text-xs sm:text-sm transition-all duration-200"
                                                        :class="{ '!border-red-500': !validateYearMonth(cert.date).isValid && cert.date }"
                                                        placeholder="YYYY 또는 YYYY.MM 또는 YYYY.MM.DD" maxlength="10" />
                                                    <CalendarIcon
                                                        class="w-4 h-4 sm:w-5 sm:h-5 text-gray-400 absolute left-2 sm:left-2.5 top-1/2 transform -translate-y-1/2" />
                                                </div>
                                            </div>
                                        </div>
                                        <button @click="removeCertification(index)"
                                            class="mt-2 sm:mt-4 text-xs sm:text-sm text-red-600 hover:text-red-700 flex items-center">
                                            <TrashIcon class="w-3 h-3 sm:w-4 sm:h-4 mr-0.5 sm:mr-1" />
                                            삭제
                                        </button>
                                    </div>
                                </div>
                            </div>

                            <!-- 포트폴리오 및 문서 파일 -->
                            <div class="mb-4 sm:mb-6 pb-4 sm:pb-6 border-b border-gray-200">
                                <div class="flex items-center justify-between mb-2 sm:mb-3">
                                    <div class="flex items-center">
                                        <h3 class="text-sm sm:text-base font-semibold text-gray-900">포트폴리오 및 문서 파일</h3>
                                        <span class="ml-2 text-xs text-gray-500">({{ documentCount }})</span>
                                    </div>
                                </div>
                                <div class="bg-white p-3 sm:p-4 rounded-md border border-gray-200">
                                    <div class="mb-4">
                                        <div class="flex flex-col sm:flex-row sm:items-center justify-between mb-2">
                                            <label class="text-sm font-medium text-gray-600">첨부 파일</label>
                                            <div class="text-xs text-gray-500 mt-1 sm:mt-0">최대 10MB, 5개까지 첨부 가능</div>
                                        </div>

                                        <div class="border-2 border-dashed border-gray-300 rounded-md p-4 text-center cursor-pointer hover:bg-gray-50 transition-colors"
                                            @click="$refs.documentInput.click()" @dragover.prevent
                                            @drop.prevent="handleFileDrop" @dragleave.prevent>
                                            <FileIcon class="w-6 h-6 sm:w-8 sm:h-8 mx-auto mb-2 text-gray-400" />
                                            <p class="text-sm text-gray-500 mb-1">파일을 드래그하거나 클릭하여 업로드</p>
                                            <p class="text-xs text-gray-400">포트폴리오, 이력서, 추천서 등</p>
                                        </div>

                                        <input type="file" ref="documentInput" class="hidden"
                                            @change="handleDocumentUpload" multiple
                                            accept=".pdf,.doc,.docx,.ppt,.pptx,.zip,.jpg,.jpeg,.png,.hwp,.xlsx" />
                                    </div>

                                    <!-- 파일 목록 -->
                                    <div v-if="showFileList" class="">
                                        <h3 class="text-sm font-medium text-gray-600 mb-2">첨부 목록 ({{ documentCount }})
                                        </h3>
                                        <div class="grid grid-cols-1 sm:grid-cols-2 gap-2">
                                            <div v-for="(doc, index) in formData.tempDocuments" :key="index"
                                                class="flex items-center justify-between p-2 sm:p-3 border border-gray-200 rounded-md bg-gray-50">
                                                <div class="flex items-center min-w-0">
                                                    <div class="mr-3 flex-shrink-0">
                                                        <FileTextIcon v-if="doc.type?.includes('pdf')"
                                                            class="w-4 h-4 sm:w-5 sm:h-5 text-red-500" />
                                                        <FileIcon
                                                            v-else-if="doc.type?.includes('word') || doc.type?.includes('doc')"
                                                            class="w-4 h-4 sm:w-5 sm:h-5 text-blue-500" />
                                                        <FileIcon
                                                            v-else-if="doc.type?.includes('presentation') || doc.type?.includes('ppt')"
                                                            class="w-4 h-4 sm:w-5 sm:h-5 text-orange-500" />
                                                        <FileArchiveIcon
                                                            v-else-if="doc.type?.includes('zip') || doc.type?.includes('archive')"
                                                            class="w-4 h-4 sm:w-5 sm:h-5 text-purple-500" />
                                                        <ImageIcon
                                                            v-else-if="doc.type?.includes('image') || doc.type?.includes('jpg') || doc.type?.includes('jpeg') || doc.type?.includes('png')"
                                                            class="w-4 h-4 sm:w-5 sm:h-5 text-sky-500" />
                                                        <FileIcon v-else-if="doc.type?.includes('hwp')"
                                                            class="w-4 h-4 sm:w-5 sm:h-5 text-green-500" />
                                                        <FileIcon
                                                            v-else-if="doc.type?.includes('sheet') || doc.type?.includes('xlsx')"
                                                            class="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500" />
                                                        <FileIcon v-else class="w-4 h-4 sm:w-5 sm:h-5 text-gray-500" />
                                                    </div>
                                                    <div class="flex-1 min-w-0">
                                                        <p
                                                            class="text-xs sm:text-sm font-medium text-gray-700 truncate">
                                                            {{
                                                                doc.name }}</p>
                                                        <p class="text-xs text-gray-500">{{ formatFileSize(doc.size) }}
                                                        </p>
                                                    </div>
                                                </div>
                                                <div class="flex items-center space-x-2 ml-4">
                                                    <button @click="removeDocument(index)"
                                                        class="text-red-500 hover:text-red-700 transition-colors p-1"
                                                        title="삭제">
                                                        <TrashIcon class="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                                                    </button>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <!-- 자기소개 -->
                            <div class="mb-4 sm:mb-6">
                                <h3 class="text-sm sm:text-base font-semibold text-gray-900">자기소개</h3>
                                <div class="p-3 sm:p-4">
                                    <textarea v-model="formData.introduction"
                                        class="w-full p-2 border border-gray-200 rounded-lg focus:outline-none focus:border-blue-500 h-20 sm:h-28 resize-none text-xs sm:text-sm"
                                        placeholder="자기소개를 입력하세요"></textarea>
                                </div>
                            </div>

                            <!-- 저장 버튼 -->
                            <div class="flex justify-center">
                                <button @click="saveResume"
                                    class="px-4 sm:px-6 py-1.5 sm:py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium text-xs sm:text-sm">
                                    저장하기
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script setup>
import { ref, computed } from 'vue'
import { useRouter } from 'vue-router'
import { 
    ArrowLeftIcon, 
    UserIcon, 
    PlusIcon, 
    TrashIcon, 
    FileIcon, 
    FileTextIcon, 
    FileArchiveIcon, 
    ImageIcon, 
    CalendarIcon 
} from 'lucide-vue-next'
import resumeAPI from '@/api/resume/resume'
import Swal from 'sweetalert2'

const router = useRouter()
const documentInput = ref(null)
const profileInput = ref(null)
const previewImage = ref(null)

const formData = ref({
    name: '',
    sex: '1',
    dob: '',
    hp: '',
    address: '',
    email: '',
    education: [],
    careers: [],
    certifications: [],
    tempDocuments: [],
    introduction: '',
    profileImageFile: null,
    profileThumbnailFile: null
})

// 유틸리티 함수들


const validateDate = (dateString) => {
    if (!dateString) return true
    const pattern = /^\d{4}\.\d{2}\.\d{2}$/
    if (!pattern.test(dateString)) return false
    
    const [year, month, day] = dateString.split('.').map(Number)
    const date = new Date(year, month - 1, day)
    
    return date.getFullYear() === year &&
           date.getMonth() === month - 1 &&
           date.getDate() === day &&
           year >= 1900 &&
           year <= new Date().getFullYear()
}

const validateYearMonth = (value) => {
    if (!value) return { value: '', isValid: true }
    
    const numericValue = value.replace(/[^0-9]/g, '')
    let formattedValue = ''
    
    if (numericValue.length > 4) {
        formattedValue = numericValue.slice(0, 4) + '.' + numericValue.slice(4, 6)
    } else {
        formattedValue = numericValue
    }
    
    if (formattedValue.length > 7) {
        formattedValue = formattedValue.slice(0, 7)
    }

    let isValid = true
    if (formattedValue.length === 7) {
        const pattern = /^\d{4}\.\d{2}$/
        if (!pattern.test(formattedValue)) {
            isValid = false
        } else {
            const year = parseInt(formattedValue.slice(0, 4))
            const month = parseInt(formattedValue.slice(5, 7))
            isValid = year >= 1900 && 
                     year <= new Date().getFullYear() && 
                     month >= 1 && 
                     month <= 12
        }
    }
    
    return {
        value: formattedValue,
        isValid
    }
}

const validateBirthDate = (event) => {
    let value = event.target.value
    value = value.replace(/[^0-9]/g, '')
    
    if (value.length > 4) {
        value = value.slice(0, 4) + '.' + value.slice(4)
        if (value.length > 7) {
            value = value.slice(0, 7) + '.' + value.slice(7)
        }
    }
    
    if (value.length > 10) {
        value = value.slice(0, 10)
    }
    
    formData.value.dob = value
}

const validatePhoneNumber = (event) => {
    let value = event.target.value
    const numbers = value.replace(/[^0-9]/g, '')
    
    if (numbers.length <= 3) {
        value = numbers
    } else if (numbers.length <= 7) {
        value = numbers.slice(0, 3) + '-' + numbers.slice(3)
    } else {
        value = numbers.slice(0, 3) + '-' + numbers.slice(3, 7) + '-' + numbers.slice(7, 11)
    }
    
    formData.value.hp = value
}

const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes'
    const k = 1024
    const sizes = ['Bytes', 'KB', 'MB', 'GB']
    const i = Math.floor(Math.log(bytes) / Math.log(k))
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

// 학력 관련 함수
const addEducation = () => {
    formData.value.education.push({
        school: '',
        major: '',
        startDate: '',
        endDate: '',
        isStudying: false
    })
}

const removeEducation = (index) => {
    formData.value.education.splice(index, 1)
}

const handleStudyingChange = (edu) => {
    if (edu.isStudying) {
        edu.endDate = '재학중'
    } else {
        edu.endDate = ''
    }
}

// 경력 관련 함수
const addCareer = () => {
    formData.value.careers.push({
        company: '',
        position: '',
        startDate: '',
        endDate: '',
        isWorking: false
    })
}

const removeCareer = (index) => {
    formData.value.careers.splice(index, 1)
}

const handleWorkingChange = (career) => {
    if (career.isWorking) {
        career.endDate = '재직중'
    } else {
        career.endDate = ''
    }
}

// 자격증 관련 함수
const addCertification = () => {
    formData.value.certifications.push({
        name: '',
        organization: '',
        date: ''
    })
}

const removeCertification = (index) => {
    formData.value.certifications.splice(index, 1)
}

// 파일 관련 함수
const handleFileUpload = async (files) => {
    if (!files || files.length === 0) return

    const newFiles = Array.from(files)
    const maxSize = 10 * 1024 * 1024 // 10MB
    let hasInvalidFile = false

    for (const file of newFiles) {
        console.log('업로드할 파일 정보:', {
            name: file.name,
            originalName: file.originalname,
            type: file.type,
            size: file.size
        })
        const fileExtension = file.name.split('.').pop().toLowerCase()
        const allowedExtensions = ['pdf', 'doc', 'docx', 'ppt', 'pptx', 'zip', 'jpg', 'jpeg', 'png', 'hwp', 'xlsx']

        if (!allowedExtensions.includes(fileExtension)) {
            await Swal.fire({
                icon: 'error',
                title: '파일 형식 오류',
                text: `${file.name} 파일은 지원하지 않는 형식입니다.`
            })
            hasInvalidFile = true
            break
        }

        if (file.size > maxSize) {
            await Swal.fire({
                icon: 'error',
                title: '파일 크기 초과',
                text: `${file.name} 파일이 10MB를 초과합니다.`
            })
            hasInvalidFile = true
            break
        }
    }

    if (hasInvalidFile) return

    const totalFiles = (formData.value.tempDocuments?.length || 0) + newFiles.length
    if (totalFiles > 5) {
        await Swal.fire({
            icon: 'warning',
            title: '파일 개수 초과',
            text: '최대 5개의 파일만 업로드할 수 있습니다.'
        })
        return
    }

    for (const file of newFiles) {
        const fileType = file.type || `application/${file.name.split('.').pop().toLowerCase()}`
        const newDoc = {
            name: file.name,
            type: fileType,
            size: file.size,
            file: file,
            file_name: file.name,
            original_name: file.name
        }

        if (!formData.value.tempDocuments) {
            formData.value.tempDocuments = []
        }
        formData.value.tempDocuments.push(newDoc)
    }
}

const handleFileDrop = (event) => {
    event.preventDefault()
    const files = event.dataTransfer.files
    if (files.length > 0) {
        handleFileUpload(files)
    }
}

const handleDocumentUpload = (event) => {
    const files = event.target.files
    if (files.length > 0) {
        handleFileUpload(files)
    }
    event.target.value = ''
}

const removeDocument = (index) => {
    formData.value.tempDocuments.splice(index, 1)
}

const showFileList = computed(() => {
    return formData.value.tempDocuments && formData.value.tempDocuments.length > 0
})

const documentCount = computed(() => {
    return formData.value.tempDocuments?.length || 0
})

// 프로필 이미지 처리
const handleProfileImageChange = async (event) => {
    const file = event.target.files[0]
    if (!file) return

    if (file.size > 10 * 1024 * 1024) {
        Swal.fire({
            icon: 'error',
            title: '파일 크기 초과',
            text: '프로필 이미지는 10MB 이하여야 합니다.'
        })
        return
    }

    if (!file.type.startsWith('image/')) {
        Swal.fire({
            icon: 'error',
            title: '파일 형식 오류',
            text: '이미지 파일만 업로드 가능합니다.'
        })
        return
    }

    try {
        const thumbnailFile = await createThumbnail(file)
        
        const reader = new FileReader()
        reader.onload = (e) => {
            previewImage.value = e.target.result
        }
        reader.readAsDataURL(file)

        formData.value.profileImageFile = file
        formData.value.profileThumbnailFile = thumbnailFile
    } catch (error) {
        console.error('이미지 처리 중 오류:', error)
        Swal.fire({
            icon: 'error',
            title: '오류',
            text: '이미지 처리 중 오류가 발생했습니다.'
        })
    }
}

const createThumbnail = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader()
        reader.readAsDataURL(file)
        reader.onload = (event) => {
            const img = new Image()
            img.src = event.target.result
            img.onload = () => {
                const canvas = document.createElement('canvas')
                const ctx = canvas.getContext('2d')
                
                const maxSize = 200
                let width = img.width
                let height = img.height
                
                if (width > height && width > maxSize) {
                    height = Math.round((height * maxSize) / width)
                    width = maxSize
                } else if (height > maxSize) {
                    width = Math.round((width * maxSize) / height)
                    height = maxSize
                }
                
                canvas.width = width
                canvas.height = height
                
                ctx.drawImage(img, 0, 0, width, height)
                
                canvas.toBlob(
                    (blob) => {
                        const thumbnailFile = new File([blob], file.name, {
                            type: 'image/jpeg',
                            lastModified: Date.now()
                        })
                        resolve(thumbnailFile)
                    },
                    'image/jpeg',
                    0.5
                )
            }
            img.onerror = reject
        }
        reader.onerror = reject
    })
}

// 이력서 저장
const saveResume = async () => {
    try {
        // 필수값 검사
        if (!formData.value.name?.trim()) {
            await Swal.fire({
                icon: 'error',
                title: '필수 입력',
                text: '이름을 입력해주세요.'
            })
            return
        }

        if (!formData.value.sex) {
            await Swal.fire({
                icon: 'error',
                title: '필수 입력',
                text: '성별을 선택해주세요.'
            })
            return
        }

        if (!formData.value.dob?.trim()) {
            await Swal.fire({
                icon: 'error',
                title: '필수 입력',
                text: '생년월일을 입력해주세요.'
            })
            return
        }

        if (!formData.value.hp?.trim()) {
            await Swal.fire({
                icon: 'error',
                title: '필수 입력',
                text: '연락처를 입력해주세요.'
            })
            return
        }

        const formDataToSend = new FormData()

        // 기본 정보
        formDataToSend.append('name', formData.value.name)
        formDataToSend.append('sex', formData.value.sex)
        formDataToSend.append('dob', formData.value.dob.replace(/\./g, ''))
        formDataToSend.append('hp', formData.value.hp.replace(/[^0-9]/g, ''))
        formDataToSend.append('address', formData.value.address || '')
        formDataToSend.append('email', formData.value.email || '')
        formDataToSend.append('note', formData.value.introduction || '')

        // 프로필 이미지
        if (formData.value.profileImageFile) {
            formDataToSend.append('profile_image', formData.value.profileImageFile)
            formDataToSend.append('profile_thumbnail', formData.value.profileThumbnailFile)
        }

        // upload_year는 한 번만 추가
        const uploadYear = new Date().getFullYear().toString()
        formDataToSend.append('upload_year', uploadYear)

        // 학력 정보
        formDataToSend.append('education_history', JSON.stringify(formData.value.education.map(edu => ({
            school: edu.school,
            major: edu.major,
            start_date: edu.startDate.replace(/\./g, ''),
            end_date: edu.isStudying ? '9999' : edu.endDate.replace(/\./g, ''),
            is_studying: edu.isStudying ? 1 : 0
        }))))

        // 경력 정보
        formDataToSend.append('career_history', JSON.stringify(formData.value.careers.map(career => ({
            company: career.company,
            position: career.position,
            start_date: career.startDate.replace(/\./g, ''),
            end_date: career.isWorking ? '9999' : career.endDate.replace(/\./g, ''),
            is_working: career.isWorking ? 1 : 0
        }))))

        // 자격증 정보
        formDataToSend.append('certification_history', JSON.stringify(formData.value.certifications.map(cert => ({
            name: cert.name,
            organization: cert.organization,
            period: cert.date ? cert.date.replace(/\./g, '') : ''
        }))))

        // 첨부파일
        if (formData.value.tempDocuments?.length > 0) {
            formData.value.tempDocuments.forEach(doc => {
                if (doc.file) {
                    formDataToSend.append('files', doc.file)
                    formDataToSend.append('file_name', doc.file.name)
                    formDataToSend.append('original_name', doc.original_name)
                    formDataToSend.append('file_size', Math.round(doc.file.size / 1024))
                }
            })
        }

        // 저장 중 로딩 표시
        const loadingToast = Swal.fire({
            title: '저장 중...',
            allowOutsideClick: false,
            didOpen: () => {
                Swal.showLoading()
            }
        })

        try {
            const response = await resumeAPI.createResume(formDataToSend)
            
            if (response.data.success) {
                await Swal.fire({
                    icon: 'success',
                    title: '성공',
                    text: '이력서가 저장되었습니다.'
                })
                router.push('/profile')
            }
        } catch (error) {
            console.error('이력서 저장 중 오류:', error)
            await Swal.fire({
                icon: 'error',
                title: '오류',
                text: '이력서 저장 중 오류가 발생했습니다.'
            })
        } finally {
            await loadingToast.close()
        }
    } catch (error) {
        console.error('이력서 저장 중 오류:', error)
        await Swal.fire({
            icon: 'error',
            title: '오류',
            text: '이력서 저장 중 오류가 발생했습니다.'
        })
    }
}

const goBack = () => {
    router.push('/profile')
}

const validateEduStartDate = (event, edu) => {
    const result = validateYearMonth(event.target.value)
    edu.startDate = result.value
}

const validateEduEndDate = (event, edu) => {
    const result = validateYearMonth(event.target.value)
    edu.endDate = result.value
}

const validateCareerStartDate = (event, career) => {
    const result = validateYearMonth(event.target.value)
    career.startDate = result.value
}

const validateCareerEndDate = (event, career) => {
    const result = validateYearMonth(event.target.value)
    career.endDate = result.value
}

const validateCertDate = (event, cert) => {
    const result = validateYearMonth(event.target.value)
    cert.date = result.value
}
</script>

<style scoped>
.fixed {
    position: fixed;
}

/* 입력 필드 포커스 스타일 */
input[type="text"],
input[type="tel"],
input[type="email"],
textarea,
select {
    @apply transition-all duration-200 focus:outline-none focus:ring-1 border-gray-200;
}

input[type="text"]:focus,
input[type="tel"]:focus,
input[type="email"]:focus,
textarea:focus,
select:focus {
    @apply ring-blue-500 border-blue-500;
}

/* 에러 상태 스타일 */
input[class*="!border-red-500"] {
    border-color: rgb(239 68 68) !important;
}

input[class*="!border-red-500"]:focus {
    border-color: rgb(239 68 68) !important;
    --tw-ring-color: rgb(239 68 68) !important;
}

/* 기본 상태 스타일 */
.border-gray-200 {
    border-color: rgb(229 231 235);
}

.border-gray-200:focus {
    border-color: rgb(59 130 246);
    --tw-ring-color: rgb(59 130 246);
}
</style>